import React, { useRef, useEffect } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import OutgoingMessage from '../ui/OutgoingMessage';
import IncomingMessage from '../ui/IncomingMessage';
import IncomingMessageLoading from '../ui/IncomingMessageLoading';
import IncomingLyrics from '../ui/IncomingLyrics';
import SongCell from '../ui/SongCell';
import MediumButton from '../ui/MediumButton';
import TypingMessage from '../ui/TypingMessage';
import { CreateIcon } from '../../utils/icons';

const ChatsView = ({ messages = [], onCreateMore, onPlaySong, onSelectSong, onTextSelect, selectedSongTitle, onOpenLyricsEditor, onOpenStylesEditor }) => {
  const scrollContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when new messages are added
  const prevMessagesLengthRef = useRef(messages.length);
  const lastMessageRef = useRef(null);
  
  useEffect(() => {
    const shouldScroll = messages.length > prevMessagesLengthRef.current;
    
    if (shouldScroll && messagesEndRef.current) {
      // Small delay to ensure DOM is updated before scrolling
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      
      return () => clearTimeout(timer);
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length]);
  
  // Also scroll when the last message type changes (for typing -> incoming transitions)
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    const lastMessageKey = lastMessage ? `${lastMessage.type}-${lastMessage.content}` : null;
    
    if (lastMessageKey !== lastMessageRef.current && lastMessage?.type === 'incoming' && messagesEndRef.current) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    lastMessageRef.current = lastMessageKey;
  }, [messages]);

  // Helper function to find song generation sequences
  const findSongSequences = () => {
    const sequences = [];
    let currentSequence = [];
    const relevantTypes = ['incoming', 'lyrics', 'players', 'createMore'];

    messages.forEach((message, index) => {
      if (relevantTypes.includes(message.type)) {
        currentSequence.push({ message, index });
      } else {
        if (currentSequence.length > 1) {
          sequences.push([...currentSequence]);
        }
        currentSequence = [];
      }
    });

    // Don't forget the last sequence
    if (currentSequence.length > 1) {
      sequences.push(currentSequence);
    }

    return sequences;
  };

  const songSequences = findSongSequences();
  const sequenceIndices = new Set(
    songSequences.flatMap(seq => seq.map(item => item.index))
  );

  const renderMessage = (message, index, isInSequence) => {
    switch (message.type) {
      case 'outgoing':
        return <OutgoingMessage message={message.content} />;

      case 'incoming':
        return <IncomingMessage message={message.content} />;

      case 'typing':
        return <TypingMessage message={message.content} onComplete={message.onComplete} />;

      case 'loading':
        return <IncomingMessageLoading />;

      case 'lyrics':
        return (
          <div
            className={message.fadeIn ? 'animate-fadeIn' : ''}
            style={{
              animation: message.fadeIn ? 'fadeIn 0.5s ease-in forwards' : 'none'
            }}
          >
            <IncomingLyrics
              lyrics={message.lyrics}
              styles={message.styles}
              onEdit={message.onEdit}
              onStylesEdit={message.onStylesEdit}
              onTextSelect={onTextSelect}
              selectedSongTitle={selectedSongTitle}
              onOpenLyricsEditor={onOpenLyricsEditor}
              onOpenStylesEditor={onOpenStylesEditor}
            />
          </div>
        );

      case 'players':
        return (
          <div
            className="flex flex-col"
            style={{
              animation: message.fadeIn ? 'fadeIn 0.5s ease-in forwards' : 'none',
              opacity: message.fadeIn ? 0 : 1
            }}
          >
            {message.songs.map((song, songIndex) => (
              <SongCell
                key={songIndex}
                title={song.title}
                genres={song.genres}
                image={song.image}
                onPlay={() => onPlaySong(song.songIndex)}
                onSelect={() => onSelectSong?.(song.songIndex)}
                onThumbsUp={song.onThumbsUp}
                onThumbsDown={song.onThumbsDown}
                onShare={song.onShare}
                onAdd={song.onAdd}
                onMore={song.onMore}
                isPlaying={song.isPlaying}
                isSelected={song.isSelected}
                isHovered={song.isHovered}
              />
            ))}
          </div>
        );

      case 'createMore':
        return (
          <div
            className="flex justify-start"
            style={{
              animation: message.fadeIn ? 'fadeIn 0.5s ease-in forwards' : 'none',
              opacity: message.fadeIn ? 0 : 1
            }}
          >
            <MediumButton
              variant="secondary"
              text="Create more"
              leftIcon={<CreateIcon />}
              onClick={onCreateMore}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={scrollContainerRef}
      className="flex flex-col p-4 pb-12 overflow-y-auto scrollbar-hide"
      style={{ backgroundColor: colors.bg.primary, scrollBehavior: 'smooth' }}
    >
      {messages.map((message, index) => {
        const isInSequence = sequenceIndices.has(index);

        // Check if this is the start of a sequence
        const isSequenceStart = songSequences.some(seq => seq[0].index === index);

        if (isSequenceStart) {
          // Find the sequence this message belongs to
          const sequence = songSequences.find(seq => seq[0].index === index);
          return (
            <div key={index} className="flex flex-col gap-4 mb-6">
              {sequence.map(({ message: seqMessage, index: seqIndex }) => (
                <div key={seqIndex}>
                  {renderMessage(seqMessage, seqIndex, true)}
                </div>
              ))}
            </div>
          );
        }

        // Skip messages that are part of a sequence but not the start
        if (isInSequence) {
          return null;
        }

        // Render standalone messages
        return (
          <div key={index} className="mb-6">
            {renderMessage(message, index, false)}
          </div>
        );
      })}
      {/* Invisible element at the end to scroll to */}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatsView;